import React from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Autocomplete,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { CertificateMetalValueCreate, MaterialType, MetalComposition } from '../types';

const EDELSTAHL_ELEMENTS = ['C', 'Mn', 'Si', 'P', 'S', 'Cr', 'Ni', 'Mo', 'Ti', 'N', 'Al', 'Cu'];
const ALU_ELEMENTS = ['Si', 'Fe', 'Cu', 'Mn', 'Zn', 'Cr', 'Mg', 'Ti', 'Al'];

interface CertificateMetalValuesFormProps {
  metalValues: CertificateMetalValueCreate[];
  onChange: (values: CertificateMetalValueCreate[]) => void;
  materialGradeCompositions: MetalComposition[];
  materialType?: MaterialType;
  disabled?: boolean;
}

type ValidationColor = 'success' | 'error' | 'warning';

interface ValidationStatus {
  isValid: boolean;
  message: string;
  color: ValidationColor;
}

function CertificateMetalValuesForm({
  metalValues,
  onChange,
  materialGradeCompositions,
  materialType,
  disabled = false,
}: CertificateMetalValuesFormProps): React.ReactElement {
  const gradeMetals = materialGradeCompositions.map((comp) => comp.metal_name);

  const allMetalsForType =
    materialType === 'EDELSTAHL'
      ? EDELSTAHL_ELEMENTS
      : ['ALU', 'HSF_PP', 'HSF_PE'].includes(materialType ?? '')
        ? ALU_ELEMENTS
        : [];

  const availableMetals = Array.from(new Set([...gradeMetals, ...allMetalsForType]));

  function getValidationStatus(metalValue: CertificateMetalValueCreate): ValidationStatus {
    if (!metalValue.metal_name.trim()) {
      return {
        isValid: false,
        message: 'Metallname erforderlich',
        color: 'error',
      };
    }

    const composition = materialGradeCompositions.find(
      (comp) => comp.metal_name.toLowerCase() === metalValue.metal_name.toLowerCase()
    );

    if (!composition) {
      return {
        isValid: false,
        message: 'Metall nicht in Materialgüte definiert',
        color: 'warning',
      };
    }

    const { measured_percentage } = metalValue;
    const { min_percentage, max_percentage } = composition;
    const isInRange =
      measured_percentage >= min_percentage && measured_percentage <= max_percentage;

    return {
      isValid: isInRange,
      message: isInRange
        ? `Im Bereich (${min_percentage}% - ${max_percentage}%)`
        : `Außerhalb des Bereichs (${min_percentage}% - ${max_percentage}%)`,
      color: isInRange ? 'success' : 'error',
    };
  }

  function getDuplicateErrors(): string[] {
    const metalNames = metalValues.map((v) => v.metal_name.toLowerCase().trim());
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const name of metalNames) {
      if (name && seen.has(name)) {
        duplicates.add(name);
      }
      seen.add(name);
    }

    return duplicates.size > 0 ? ['Metalle dürfen nicht doppelt vorkommen'] : [];
  }

  function handleMetalValueChange(
    index: number,
    field: keyof CertificateMetalValueCreate,
    value: string | number
  ): void {
    const newValues = [...metalValues];

    if (field === 'metal_name') {
      newValues[index][field] = value as string;
    } else {
      newValues[index][field] = Number(value);
    }

    onChange(newValues);
  }

  function addMetalValue(): void {
    onChange([...metalValues, { metal_name: '', measured_percentage: 0 }]);
  }

  function removeMetalValue(index: number): void {
    onChange(metalValues.filter((_, i) => i !== index));
  }

  function addAllRequiredMetals(): void {
    const existingMetals = metalValues.map((v) => v.metal_name.toLowerCase());
    const newValues = [...metalValues];

    materialGradeCompositions.forEach((comp) => {
      if (!existingMetals.includes(comp.metal_name.toLowerCase())) {
        newValues.push({ metal_name: comp.metal_name, measured_percentage: 0 });
      }
    });

    onChange(newValues);
  }

  function addAllTypeMetals(): void {
    const existingMetals = metalValues.map((v) => v.metal_name.toLowerCase());
    const newValues = [...metalValues];

    allMetalsForType.forEach((metal) => {
      if (!existingMetals.includes(metal.toLowerCase())) {
        newValues.push({ metal_name: metal, measured_percentage: 0 });
      }
    });

    onChange(newValues);
  }

  const errors = getDuplicateErrors();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">Gemessene Metallwerte</Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {allMetalsForType.length > 0 && (
            <Button type="button" variant="outlined" size="small" onClick={addAllTypeMetals} disabled={disabled}>
              Alle Metalle ({materialType === 'EDELSTAHL' ? 'Edelstahl' : 'Alu'})
            </Button>
          )}
          {materialGradeCompositions.length > 0 && (
            <Button type="button" variant="outlined" size="small" onClick={addAllRequiredMetals} disabled={disabled}>
              Metalle lt. Materialgüte
            </Button>
          )}
          <Button type="button" variant="outlined" startIcon={<AddIcon />} onClick={addMetalValue} disabled={disabled} size="small">
            Hinzufügen
          </Button>
        </Box>
      </Box>

      {materialGradeCompositions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {materialGradeCompositions.map((comp, index) => (
            <Chip
              key={index}
              label={`${comp.metal_name} ${comp.min_percentage}–${comp.max_percentage}%`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {metalValues.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Keine Metallwerte. „Hinzufügen“ oder Metalle lt. Materialgüte.
        </Typography>
      ) : (
        <Box>
          {metalValues.map((metalValue, index) => {
            const validationStatus = getValidationStatus(metalValue);
            return (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 90px 36px', sm: 'minmax(120px,1.1fr) 110px minmax(120px,1.4fr) 36px' },
                  gap: 1,
                  alignItems: 'center',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Autocomplete
                  value={metalValue.metal_name}
                  onChange={(_, newValue) => handleMetalValueChange(index, 'metal_name', newValue || '')}
                  onInputChange={(_, newInputValue) =>
                    handleMetalValueChange(index, 'metal_name', newInputValue)
                  }
                  options={availableMetals}
                  freeSolo
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Metall"
                      required
                      error={!metalValue.metal_name.trim() && metalValues.length > 0}
                    />
                  )}
                />
                <TextField
                  type="number"
                  label="Ist %"
                  value={metalValue.measured_percentage}
                  onChange={(e) => handleMetalValueChange(index, 'measured_percentage', e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.0001 }}
                  disabled={disabled}
                  error={metalValue.measured_percentage < 0 || metalValue.measured_percentage > 100}
                />
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {metalValue.metal_name.trim() && (
                    <>
                      {validationStatus.isValid ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <ErrorIcon color={validationStatus.color} fontSize="small" />
                      )}
                      <Typography variant="caption" color={`${validationStatus.color}.main`} noWrap>
                        {validationStatus.message}
                      </Typography>
                    </>
                  )}
                </Box>
                <IconButton
                  onClick={() => removeMetalValue(index)}
                  disabled={disabled}
                  color="error"
                  size="small"
                  title="Metallwert entfernen"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default CertificateMetalValuesForm;
